import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import {
  FiArrowRight,
  FiAward,
  FiHeadphones,
  FiInstagram,
  FiLinkedin,
  FiShield,
  FiShoppingBag,
  FiTruck,
  FiTwitter,
  FiUsers
} from 'react-icons/fi';
import { db } from '../utils/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import { useHeroReveal, useScrollReveal } from '../hooks/useGsapMotion';

import fastDeliveryIcon from '../assets/contact/fast-delivery.png';
import customerServiceIcon from '../assets/contact/customer_sevice.png';
import moneyBackIcon from '../assets/contact/check-mark.png';

const STORY_IMAGE_FALLBACK =
  'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1400&q=80';
const TEAM_IMAGE_FALLBACK =
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80';

const FALLBACK_ABOUT = {
  title: 'Our Story',
  description: [
    'ShopCircuit began with a clear goal: make premium shopping simple, transparent, and dependable for every customer.',
    'Today, we combine curated products, fair pricing, and disciplined operations to deliver a seamless commerce experience from discovery to doorstep.'
  ],
  image: STORY_IMAGE_FALLBACK
};

const FALLBACK_STATS = [
  { id: 'sellers', number: '10.5k+', label: 'Active sellers', iconKey: 'sellers' },
  { id: 'products', number: '1M+', label: 'Products listed', iconKey: 'products' },
  { id: 'customers', number: '3M+', label: 'Customers served', iconKey: 'customers' },
  { id: 'support', number: '24/7', label: 'Support operations', iconKey: 'support' }
];

const FALLBACK_TEAM = [
  {
    id: 'm1',
    name: 'Arafat Khan',
    role: 'Founder & Product Lead',
    image:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80',
    social: {
      twitter: 'https://twitter.com',
      linkedin: 'https://linkedin.com',
      instagram: 'https://instagram.com'
    }
  },
  {
    id: 'm2',
    name: 'Nabila Islam',
    role: 'Brand & Experience Director',
    image:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=900&q=80',
    social: {
      twitter: 'https://twitter.com',
      linkedin: 'https://linkedin.com',
      instagram: 'https://instagram.com'
    }
  },
  {
    id: 'm3',
    name: 'Raihan Ahmed',
    role: 'Operations & Fulfillment',
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&q=80',
    social: {
      twitter: 'https://twitter.com',
      linkedin: 'https://linkedin.com',
      instagram: 'https://instagram.com'
    }
  }
];

const normalizeAboutData = (rawData) => {
  const description = (Array.isArray(rawData?.description) ? rawData.description : [rawData?.description])
    .filter(Boolean)
    .map((line) => String(line).trim())
    .filter((line) => line.length >= 28);
  const title = String(rawData?.title || '').trim();
  const useProvidedDescription = description.length >= 2 && description.join(' ').length >= 110;
  const useProvidedTitle = title.length >= 6;

  return {
    ...FALLBACK_ABOUT,
    ...rawData,
    title: useProvidedTitle ? title : FALLBACK_ABOUT.title,
    description: useProvidedDescription ? description : FALLBACK_ABOUT.description,
    image: FALLBACK_ABOUT.image
  };
};

const inferIconKey = (label = '') => {
  const lower = String(label).toLowerCase();
  if (lower.includes('seller') || lower.includes('vendor') || lower.includes('store')) return 'sellers';
  if (lower.includes('product') || lower.includes('inventory')) return 'products';
  if (lower.includes('customer') || lower.includes('client') || lower.includes('order')) return 'customers';
  if (lower.includes('support') || lower.includes('service')) return 'support';
  return 'products';
};

const About = () => {
  const [aboutData, setAboutData] = useState(FALLBACK_ABOUT);
  const [statistics, setStatistics] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageRef = useRef(null);

  useHeroReveal(pageRef, [loading]);
  useScrollReveal(pageRef, [statistics.length, teamMembers.length]);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        setLoading(true);
        setError('');

        const aboutDoc = await getDoc(doc(db, 'pages', 'about'));
        if (aboutDoc.exists()) {
          setAboutData(normalizeAboutData(aboutDoc.data()));
        } else {
          setAboutData(FALLBACK_ABOUT);
        }

        const statsSnapshot = await getDocs(collection(db, 'about-statistics'));
        const statsData = statsSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setStatistics(statsData);

        const teamSnapshot = await getDocs(collection(db, 'team-members'));
        const teamData = teamSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setTeamMembers(teamData);
      } catch (fetchError) {
        console.error('Failed to load about page data:', fetchError);
        setError(fetchError.message || 'Failed to load full content');
        setAboutData(FALLBACK_ABOUT);
        setStatistics([]);
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  const resolvedStats = useMemo(
    () => (statistics.length ? statistics : FALLBACK_STATS),
    [statistics]
  );
  const resolvedTeam = useMemo(
    () => (teamMembers.length ? teamMembers : FALLBACK_TEAM),
    [teamMembers]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div ref={pageRef} className="sc-container pb-16 pt-8 md:pt-10">
      <nav className="mb-7 flex items-center gap-2 text-sm text-slate-500" data-hero-item>
        <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
          Home
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-700">About</span>
      </nav>

      {error && (
        <div className="mb-7 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-hero-item>
          Showing fallback content while database sync is unavailable.
        </div>
      )}

      <section className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-hero-item>
        <div className="lg:col-span-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Who We Are</p>
          <h1 className="text-4xl font-black leading-tight text-[#1A1A1A] sm:text-5xl">{aboutData.title}</h1>
          <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600 sm:text-base">
            {aboutData.description.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/products" className="sc-btn-primary sc-hover-smooth inline-flex items-center gap-2">
              Explore Products
              <FiArrowRight size={14} />
            </Link>
            <Link to="/contact" className="sc-btn-ghost sc-hover-smooth">
              Contact Team
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <img
              src={aboutData.image || STORY_IMAGE_FALLBACK}
              alt={aboutData.title}
              className="sc-hover-smooth h-[320px] w-full object-cover sm:h-[460px] group-hover:scale-[1.03]"
              onError={(event) => {
                event.currentTarget.src = STORY_IMAGE_FALLBACK;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/20 bg-white/12 p-4 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Design-First Commerce</p>
              <p className="mt-2 text-sm text-white/90">
                Reliable operations, curated product quality, and a buying experience built to convert.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-stagger>
        {resolvedStats.map((stat, index) => (
          <article
            key={stat.id || index}
            className="sc-soft-card sc-hover-smooth p-5 text-center hover:-translate-y-1 hover:border-[#DB4444]/30"
            data-stagger-item
          >
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[#DB4444]/10 text-[#DB4444]">
              <StatVisual stat={stat} index={index} />
            </div>
            <p className="text-2xl font-black text-[#1A1A1A]">{stat.number || stat.value || '-'}</p>
            <p className="mt-1 text-sm font-medium text-slate-600">{stat.label || stat.title || 'Metric'}</p>
          </article>
        ))}
      </section>

      <section className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3" data-reveal>
        <ValueCard
          icon={<FiTruck size={18} />}
          title="Fast Logistics"
          description="Optimized warehouse and courier pipelines for dependable dispatch."
        />
        <ValueCard
          icon={<FiShield size={18} />}
          title="Trusted Quality"
          description="Vetted listings, transparent pricing and reliable after-sales policy."
        />
        <ValueCard
          icon={<FiHeadphones size={18} />}
          title="Human Support"
          description="Live support coverage for checkout, shipping and return requests."
        />
      </section>

      <section className="mt-16" data-reveal>
        <div className="mb-7">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">People</p>
          <h2 className="text-3xl font-black text-[#1A1A1A]">Meet Our Team</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {resolvedTeam.map((member) => (
            <article key={member.id} className="sc-soft-card overflow-hidden">
              <div className="group h-64 overflow-hidden bg-slate-100">
                <img
                  src={member.image || TEAM_IMAGE_FALLBACK}
                  alt={member.name}
                  className="sc-hover-smooth h-full w-full object-cover group-hover:scale-[1.03]"
                  onError={(event) => {
                    event.currentTarget.src = TEAM_IMAGE_FALLBACK;
                  }}
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-[#1A1A1A]">{member.name}</h3>
                <p className="mt-1 text-sm text-slate-600">{member.role || 'Team Member'}</p>
                <div className="mt-4 flex gap-2">
                  {member.social?.twitter && (
                    <a
                      href={member.social.twitter}
                      target="_blank"
                      rel="noreferrer"
                      className="sc-hover-smooth inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-[#DB4444] hover:text-[#DB4444]"
                      aria-label={`${member.name} Twitter`}
                    >
                      <FiTwitter size={14} />
                    </a>
                  )}
                  {member.social?.instagram && (
                    <a
                      href={member.social.instagram}
                      target="_blank"
                      rel="noreferrer"
                      className="sc-hover-smooth inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-[#DB4444] hover:text-[#DB4444]"
                      aria-label={`${member.name} Instagram`}
                    >
                      <FiInstagram size={14} />
                    </a>
                  )}
                  {member.social?.linkedin && (
                    <a
                      href={member.social.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="sc-hover-smooth inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-[#DB4444] hover:text-[#DB4444]"
                      aria-label={`${member.name} LinkedIn`}
                    >
                      <FiLinkedin size={14} />
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-16" data-reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <ServiceCard
            icon={fastDeliveryIcon}
            title="Free and Fast Delivery"
            description="Free delivery for orders over BDT 1,400."
          />
          <ServiceCard
            icon={customerServiceIcon}
            title="24/7 Customer Service"
            description="Our support team is online every day."
          />
          <ServiceCard
            icon={moneyBackIcon}
            title="Money Back Guarantee"
            description="Easy refund process within 30 days."
          />
        </div>
      </section>
    </div>
  );
};

const StatVisual = ({ stat, index }) => {
  if (stat.image) {
    return (
      <img
        src={stat.image}
        alt={stat.label || stat.title || 'Statistic'}
        className="h-6 w-6 object-contain"
        onError={(event) => {
          event.currentTarget.style.display = 'none';
        }}
      />
    );
  }

  if (typeof stat.icon === 'string' && stat.icon.trim() && stat.icon.trim().length <= 2) {
    return <span className="text-lg">{stat.icon}</span>;
  }

  const iconKey = stat.iconKey || inferIconKey(stat.label || stat.title || index);
  if (iconKey === 'sellers') return <FiShoppingBag size={17} />;
  if (iconKey === 'customers') return <FiUsers size={17} />;
  if (iconKey === 'support') return <FiHeadphones size={17} />;
  return <FiAward size={17} />;
};

const ValueCard = ({ icon, title, description }) => (
  <article className="sc-soft-card p-5">
    <span className="mb-4 inline-flex rounded-full bg-[#DB4444]/10 p-2 text-[#DB4444]">{icon}</span>
    <h3 className="text-lg font-bold text-[#1A1A1A]">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
  </article>
);

const ServiceCard = ({ icon, title, description }) => (
  <article className="text-center">
    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#DB4444]">
      <img src={icon} alt={title} className="h-9 w-9 object-contain brightness-0 invert" />
    </div>
    <h3 className="text-lg font-bold uppercase text-[#1A1A1A]">{title}</h3>
    <p className="mt-1 text-sm text-slate-600">{description}</p>
  </article>
);

export default About;
